<template>
  <div>
    <spinner-loader :loadingStuff="loadingStuff" />
    <app-template />
    <div class="uk-container uk-container-large physio-calendar-container">
      <!-- Header -->
      <div class="page-header">
        <div class="header-left">
          <h2><i class="bi bi-calendar2-heart"></i> Physio Calendar</h2>
          <p class="subtitle">Manage availability and appointments</p>
        </div>
        <div class="header-right">
          <!-- Physio Selector (for Admin) -->
          <div v-if="canSelectPhysio" class="physio-selector">
            <label>Viewing:</label>
            <select v-model="selectedPhysioId" class="form-select" @change="loadCalendarData">
              <option v-for="physio in physios" :key="physio.id" :value="physio.id">
                {{ physio.name }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Week Navigation -->
      <div class="week-navigation">
        <button class="btn btn-outline-primary" @click="previousWeek">
          <i class="bi bi-chevron-left"></i> Previous
        </button>
        <div class="current-week">
          <i class="bi bi-calendar3"></i>
          {{ formatWeekRange }}
        </div>
        <button class="btn btn-outline-primary" @click="nextWeek">
          Next <i class="bi bi-chevron-right"></i>
        </button>
        <button class="btn btn-link" @click="goToToday">Today</button>
      </div>

      <!-- View Toggle -->
      <div class="view-controls">
        <div class="btn-group">
          <button :class="['btn', viewMode === 'calendar' ? 'btn-primary' : 'btn-outline-primary']"
            @click="viewMode = 'calendar'">
            <i class="bi bi-grid-3x3"></i> Calendar
          </button>
          <button :class="['btn', viewMode === 'availability' ? 'btn-primary' : 'btn-outline-primary']"
            @click="viewMode = 'availability'">
            <i class="bi bi-clock"></i> Manage Availability
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <div class="spinner-border text-primary" role="status"></div>
        <p>Loading calendar...</p>
      </div>

      <!-- No Physios Found -->
      <div v-else-if="physios.length === 0" class="empty-state">
        <i class="bi bi-calendar-x" style="font-size: 3rem; color: #6c757d;"></i>
        <h4>No Physios Found</h4>
        <p v-if="isAdmin">There are no physios registered in your club yet. Add users with the "Physio" or "Head Physio"
          position to use this calendar.</p>
        <p v-else>No physio calendars are available at this time.</p>
      </div>

      <!-- Calendar View -->
      <div v-else-if="viewMode === 'calendar'" class="calendar-view">

        <!-- Mobile Day Selector -->
        <div v-if="isMobile" class="mobile-day-selector">
          <button class="day-nav-btn" @click="previousDay" :disabled="selectedDayIndex === 0">
            <i class="bi bi-chevron-left"></i>
          </button>
          <div class="day-tabs">
            <button v-for="(day, index) in weekDays" :key="day.date" class="day-tab" :class="{
              'active': selectedDayIndex === index,
              'is-today': isToday(day.date),
              'is-past': isPast(day.date)
            }" @click="selectDay(index)">
              <span class="day-abbr">{{ day.dayName.substring(0, 3) }}</span>
              <span class="day-num">{{ day.displayDate.split('/')[0] }}</span>
            </button>
          </div>
          <button class="day-nav-btn" @click="nextDay" :disabled="selectedDayIndex === weekDays.length - 1">
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>

        <!-- Mobile Single Day View -->
        <div v-if="isMobile" class="mobile-calendar">
          <div class="mobile-day-header">
            <span class="day-name">{{ selectedDay.dayName }}</span>
            <span class="day-date">{{ selectedDay.displayDate }}</span>
          </div>
          <div class="mobile-slots-list">
            <div v-for="hour in displayHours" :key="hour" class="mobile-hour-row">
              <div class="hour-label">{{ formatHour(hour) }}</div>
              <div class="hour-slots">
                <template v-for="slot in getSlotsForHour(selectedDay.date, hour)" :key="slot.id || slot.start_time">
                  <div v-if="slot.type === 'available'" class="mobile-slot available"
                    @click="handleSlotClick(selectedDay.date, slot)">
                    <i class="bi bi-clock"></i>
                    <span class="time">{{ formatTime(slot.start_time) }} - {{ formatTime(slot.end_time) }}</span>
                    <span class="status">Available</span>
                  </div>
                  <div v-else-if="slot.type === 'booked'" class="mobile-slot booked" @click="viewAppointment(slot)">
                    <i class="bi bi-person-fill"></i>
                    <div class="slot-info">
                      <span class="time">{{ formatTime(slot.start_time) }} - {{ formatTime(slot.end_time) }}</span>
                      <span class="patient">{{ slot.player_name }}</span>
                    </div>
                    <span class="status">Booked</span>
                  </div>
                </template>
                <div v-if="getSlotsForHour(selectedDay.date, hour).length === 0" class="no-slots-hour">
                  -
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop Week Grid (hidden on mobile) -->
        <div v-if="!isMobile" class="calendar-grid desktop-calendar">
          <!-- Time Column -->
          <div class="time-column">
            <div class="day-header"></div>
            <div v-for="hour in displayHours" :key="hour" class="time-slot">
              {{ formatHour(hour) }}
            </div>
          </div>

          <!-- Day Columns -->
          <div v-for="day in weekDays" :key="day.date" class="day-column"
            :class="{ 'is-today': isToday(day.date), 'is-past': isPast(day.date) }">
            <div class="day-header">
              <span class="day-name">{{ day.dayName }}</span>
              <span class="day-date">{{ day.displayDate }}</span>
            </div>

            <!-- Time Slots for this day -->
            <div class="slots-container">
              <div v-for="hour in displayHours" :key="hour" class="hour-block">
                <!-- Check for availability/appointments in this hour -->
                <template v-for="slot in getSlotsForHour(day.date, hour)" :key="slot.id || slot.start_time">
                  <div v-if="slot.type === 'available'" class="slot available-slot" :style="getSlotStyle(slot, hour)"
                    @click="handleSlotClick(day.date, slot)">
                    <span class="slot-time">{{ formatTime(slot.start_time) }}</span>
                    <span class="slot-label">Available</span>
                  </div>
                  <div v-else-if="slot.type === 'booked'" class="slot booked-slot" :style="getSlotStyle(slot, hour)"
                    @click="viewAppointment(slot)">
                    <span class="slot-time">{{ formatTime(slot.start_time) }}</span>
                    <span class="slot-patient">{{ slot.player_name }}</span>
                    <span v-if="slot.reason" class="slot-reason">{{ slot.reason }}</span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Availability Management View -->
      <div v-else-if="viewMode === 'availability'" class="availability-view">
        <div class="availability-header">
          <h4>Manage Availability for {{ selectedPhysioName }}</h4>
          <p class="text-muted">Click on a day to add availability, or remove existing slots</p>
        </div>

        <!-- Quick Add Form -->
        <div class="quick-add-section">
          <h5><i class="bi bi-plus-circle"></i> Add Availability</h5>

          <!-- Mode Toggle -->
          <div class="mode-toggle">
            <button :class="['btn', !bulkMode ? 'btn-primary' : 'btn-outline-primary']" @click="bulkMode = false">
              <i class="bi bi-calendar-day"></i> Single Day
            </button>
            <button :class="['btn', bulkMode ? 'btn-primary' : 'btn-outline-primary']" @click="bulkMode = true">
              <i class="bi bi-calendar-range"></i> Bulk Add
            </button>
          </div>

          <div class="quick-add-form">
            <!-- Single Day Mode -->
            <div v-if="!bulkMode" class="form-row">
              <div class="form-group">
                <label>Date</label>
                <VueDatePicker v-model="newSlot.date" :enable-time-picker="false" format="dd/MM/yyyy" :locale="'en-GB'"
                  :min-date="new Date()" placeholder="Select date" />
              </div>
              <div class="form-group">
                <label>Start Time</label>
                <select v-model="newSlot.startTime" class="form-select">
                  <option v-for="time in timeOptions" :key="time" :value="time">{{ formatTime(time) }}</option>
                </select>
              </div>
              <div class="form-group">
                <label>End Time</label>
                <select v-model="newSlot.endTime" class="form-select">
                  <option v-for="time in endTimeOptions" :key="time" :value="time">{{ formatTime(time) }}</option>
                </select>
              </div>
              <div class="form-group">
                <label>Slot Length</label>
                <select v-model="newSlot.slotLength" class="form-select">
                  <option :value="10">10 min</option>
                  <option :value="15">15 min</option>
                  <option :value="20">20 min</option>
                  <option :value="30">30 min</option>
                  <option :value="45">45 min</option>
                  <option :value="60">60 min</option>
                </select>
              </div>
              <div class="form-group form-group-btn">
                <button class="btn btn-success" @click="addAvailabilitySlots" :disabled="!canAddSlot">
                  <i class="bi bi-plus"></i> Add Slots
                </button>
              </div>
            </div>

            <!-- Bulk Mode -->
            <div v-else class="bulk-add-form">
              <div class="form-row">
                <div class="form-group">
                  <label>Date Range</label>
                  <VueDatePicker v-model="bulkSlot.dateRange" range :enable-time-picker="false" format="dd/MM/yyyy"
                    :locale="'en-GB'" :min-date="new Date()" placeholder="Select date range" />
                </div>
                <div class="form-group">
                  <label>Start Time</label>
                  <select v-model="bulkSlot.startTime" class="form-select">
                    <option v-for="time in timeOptions" :key="time" :value="time">{{ formatTime(time) }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>End Time</label>
                  <select v-model="bulkSlot.endTime" class="form-select">
                    <option v-for="time in bulkEndTimeOptions" :key="time" :value="time">{{ formatTime(time) }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Slot Length</label>
                  <select v-model="bulkSlot.slotLength" class="form-select">
                    <option :value="10">10 min</option>
                    <option :value="15">15 min</option>
                    <option :value="20">20 min</option>
                    <option :value="30">30 min</option>
                    <option :value="45">45 min</option>
                    <option :value="60">60 min</option>
                  </select>
                </div>
              </div>

              <div class="days-selection">
                <label>Apply to days:</label>
                <div class="day-checkboxes">
                  <label v-for="(day, index) in dayNames" :key="index" class="day-checkbox">
                    <input type="checkbox" v-model="bulkSlot.selectedDays" :value="index" />
                    <span>{{ day }}</span>
                  </label>
                </div>
                <div class="quick-select-btns">
                  <button type="button" class="btn btn-sm btn-outline-secondary"
                    @click="selectWeekdays">Weekdays</button>
                  <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectAllDays">All
                    Days</button>
                  <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearDays">Clear</button>
                </div>
              </div>

              <div class="bulk-preview" v-if="bulkPreviewCount > 0">
                <i class="bi bi-info-circle"></i>
                This will create availability for <strong>{{ bulkPreviewCount }}</strong> day(s)
              </div>

              <div class="form-group form-group-btn">
                <button class="btn btn-success" @click="addBulkAvailability" :disabled="!canAddBulkSlot">
                  <i class="bi bi-plus"></i> Add Bulk Availability
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Current Week Availability -->
        <div class="week-availability">
          <h5><i class="bi bi-calendar-week"></i> This Week's Availability</h5>
          <div class="week-grid">
            <div v-for="day in weekDays" :key="day.date" class="day-card" :class="{ 'is-past': isPast(day.date) }">
              <div class="day-card-header">
                <span class="day-name">{{ day.dayName }}</span>
                <span class="day-date">{{ day.displayDate }}</span>
              </div>
              <div class="day-card-body">
                <div v-if="getAvailabilityForDay(day.date).length === 0" class="no-slots">
                  <i class="bi bi-calendar-x"></i>
                  <span>No availability</span>
                </div>
                <div v-else class="slot-list">
                  <div v-for="slot in getAvailabilityForDay(day.date)" :key="slot.id" class="slot-item"
                    :class="{ 'is-booked': isSlotBooked(day.date, slot.start_time) }">
                    <span class="slot-time">{{ formatTime(slot.start_time) }} - {{ formatTime(slot.end_time) }}</span>
                    <button v-if="!isSlotBooked(day.date, slot.start_time) && !isPast(day.date)"
                      class="btn btn-sm btn-outline-danger" @click="confirmRemoveSlot(day.date, slot)">
                      <i class="bi bi-x"></i>
                    </button>
                    <span v-else-if="isSlotBooked(day.date, slot.start_time)" class="booked-badge">
                      Booked
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Bulk Actions -->
        <div class="bulk-actions">
          <h5><i class="bi bi-lightning"></i> Bulk Actions</h5>
          <div class="bulk-buttons">
            <button class="btn btn-outline-danger" @click="confirmClearWeek">
              <i class="bi bi-trash"></i> Clear All Unbooked Slots This Week
            </button>
          </div>
        </div>
      </div>

      <!-- Appointment Modal -->
      <div v-if="showAppointmentModal" class="modal-overlay">
        <div class="modal-dialog appointment-modal">
          <div class="modal-header">
            <h4>{{ isBookingMode ? 'Book Appointment' : 'Appointment Details' }}</h4>
            <button class="close-btn" @click="closeAppointmentModal">&times;</button>
          </div>
          <div class="modal-body">
            <template v-if="isBookingMode">
              <div class="booking-info">
                <p><strong>Date:</strong> {{ formatDateFull(selectedSlot?.date) }}</p>
                <p><strong>Time:</strong> {{ formatTime(selectedSlot?.start_time) }} - {{
                  formatTime(selectedSlot?.end_time) }}</p>
                <p><strong>Physio:</strong> {{ selectedPhysioName }}</p>
              </div>

              <!-- Player Selection for Admin/Physio -->
              <div v-if="canBookForOthers" class="form-group">
                <label>Book for Player <span class="text-danger">*</span></label>
                <select v-model="bookingForm.playerId" class="form-select" required>
                  <option value="">Select a player...</option>
                  <optgroup label="Players with Active Injuries">
                    <option v-for="player in injuredPlayers" :key="player.id" :value="player.id">
                      {{ player.name }} - {{ player.injury_type || 'Injured' }}
                    </option>
                  </optgroup>
                  <optgroup label="All Players">
                    <option v-for="player in allPlayers" :key="'all-' + player.id" :value="player.id">
                      {{ player.name }}
                    </option>
                  </optgroup>
                </select>
              </div>

              <div class="form-group">
                <label>Reason for appointment</label>
                <select v-model="bookingForm.reason" class="form-select">
                  <option value="">Select reason...</option>
                  <option value="Follow-up">Follow-up</option>
                  <option value="Assessment">Assessment</option>
                  <option value="Treatment">Treatment</option>
                  <option value="Rehabilitation">Rehabilitation</option>
                  <option value="Pre-season screening">Pre-season screening</option>
                  <option value="Return to play assessment">Return to play assessment</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div class="form-group">
                <label>Notes (optional)</label>
                <textarea v-model="bookingForm.notes" class="form-control" rows="3"
                  placeholder="Any additional information..."></textarea>
              </div>
              <button class="btn btn-success btn-block" @click="bookAppointment"
                :disabled="bookingLoading || (canBookForOthers && !bookingForm.playerId)">
                <span v-if="bookingLoading"><span class="spinner-border spinner-border-sm"></span> Booking...</span>
                <span v-else><i class="bi bi-check-circle"></i> Confirm Booking</span>
              </button>
            </template>
            <template v-else>
              <div class="appointment-details">
                <div class="detail-row">
                  <label>Patient:</label>
                  <span>{{ selectedAppointment?.player_name }}</span>
                </div>
                <div class="detail-row">
                  <label>Date:</label>
                  <span>{{ formatDateFull(selectedAppointment?.appointment_date) }}</span>
                </div>
                <div class="detail-row">
                  <label>Time:</label>
                  <span>{{ formatTime(selectedAppointment?.start_time) }} - {{ formatTime(selectedAppointment?.end_time)
                    }}</span>
                </div>
                <div class="detail-row">
                  <label>Reason:</label>
                  <span>{{ selectedAppointment?.reason || 'Not specified' }}</span>
                </div>
                <div v-if="selectedAppointment?.notes" class="detail-row">
                  <label>Notes:</label>
                  <span>{{ selectedAppointment.notes }}</span>
                </div>
                <div class="detail-row">
                  <label>Status:</label>
                  <span class="status-badge" :class="selectedAppointment?.status">{{ selectedAppointment?.status
                    }}</span>
                </div>
              </div>
              <div class="appointment-actions">
                <button class="btn btn-secondary" @click="closeAppointmentModal">
                  <i class="bi bi-arrow-left"></i> Close
                </button>
                <button class="btn btn-outline-primary" @click="openEditAppointmentModal">
                  <i class="bi bi-pencil"></i> Edit
                </button>
                <button class="btn btn-outline-danger" @click="confirmCancelAppointment">
                  <i class="bi bi-x-circle"></i> Cancel Appointment
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Confirmation Modal -->
      <div v-if="showConfirmModal" class="modal-overlay">
        <div class="modal-dialog confirm-modal">
          <div class="modal-header">
            <h4>{{ confirmModal.title }}</h4>
            <button class="close-btn" @click="closeConfirmModal">&times;</button>
          </div>
          <div class="modal-body">
            <p>{{ confirmModal.message }}</p>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeConfirmModal">Cancel</button>
            <button class="btn" :class="confirmModal.btnClass" @click="confirmModal.action()">
              {{ confirmModal.btnText }}
            </button>
          </div>
        </div>
      </div>

      <!-- Edit Appointment Modal -->
      <div v-if="showEditAppointmentModal" class="modal-overlay" @click="handleEditOverlayClick">
        <div class="modal-dialog edit-appointment-modal" @click.stop>
          <div class="modal-header">
            <h4><i class="bi bi-pencil-square"></i> Edit Appointment</h4>
            <button class="close-btn" @click="closeEditAppointmentModal">&times;</button>
          </div>
          <div class="modal-body">
            <!-- Current Appointment Info -->
            <div class="current-appointment-info">
              <h6>Current Appointment</h6>
              <div class="info-row">
                <i class="bi bi-person"></i>
                <span>{{ editingAppointmentData?.player_name }}</span>
              </div>
              <div class="info-row">
                <i class="bi bi-calendar"></i>
                <span>{{ formatDateFull(editingAppointmentData?.appointment_date) }}</span>
              </div>
              <div class="info-row">
                <i class="bi bi-clock"></i>
                <span>{{ formatTime(editingAppointmentData?.start_time) }} - {{
                  formatTime(editingAppointmentData?.end_time)
                }}</span>
              </div>
            </div>

            <!-- Reschedule Section -->
            <div class="reschedule-section">
              <div class="form-check mb-3">
                <input type="checkbox" class="form-check-input" id="wantRescheduleAdmin"
                  v-model="editAppointmentForm.wantReschedule">
                <label class="form-check-label" for="wantRescheduleAdmin">
                  <strong>Reschedule to a different time</strong>
                </label>
              </div>

              <div v-if="editAppointmentForm.wantReschedule" class="reschedule-options">
                <div class="form-group">
                  <label>Select New Date</label>
                  <input type="date" class="form-control" v-model="editAppointmentForm.newDate"
                    :min="minRescheduleDate">
                </div>

                <div v-if="editAppointmentForm.newDate && rescheduleAvailability.length > 0" class="form-group">
                  <label>Available Time Slots</label>
                  <div class="time-slots-grid">
                    <button v-for="slot in rescheduleAvailability" :key="slot.start_time" type="button"
                      :class="['time-slot-btn', { selected: editAppointmentForm.newStartTime === slot.start_time }]"
                      @click="selectRescheduleSlot(slot)">
                      {{ formatTime(slot.start_time) }}
                    </button>
                  </div>
                </div>

                <div v-else-if="editAppointmentForm.newDate && !loadingRescheduleSlots" class="no-slots-message">
                  <i class="bi bi-exclamation-circle"></i>
                  No available slots for this date. Please select another date.
                </div>

                <div v-if="loadingRescheduleSlots" class="loading-slots">
                  <span class="spinner-border spinner-border-sm"></span> Loading availability...
                </div>
              </div>
            </div>

            <!-- Reason and Notes -->
            <div class="form-group">
              <label>Reason for appointment</label>
              <select v-model="editAppointmentForm.reason" class="form-select">
                <option value="">Select reason...</option>
                <option value="Follow-up">Follow-up</option>
                <option value="Assessment">Assessment</option>
                <option value="Treatment">Treatment</option>
                <option value="Rehabilitation">Rehabilitation</option>
                <option value="Pre-season screening">Pre-season screening</option>
                <option value="Return to play assessment">Return to play assessment</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div class="form-group">
              <label>Notes</label>
              <textarea v-model="editAppointmentForm.notes" class="form-control" rows="3"
                placeholder="Any additional information..."></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeEditAppointmentModal">Cancel</button>
            <button class="btn btn-primary" @click="saveEditedAppointment"
              :disabled="editLoading || !canSaveAppointmentEdit">
              <span v-if="editLoading"><span class="spinner-border spinner-border-sm"></span></span>
              <span v-else><i class="bi bi-check-circle"></i> Save Changes</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppTemplate from "../layouts/App";
import SpinnerLoader from "../../components/LoadingSpinning";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { PhysioCalendarService } from "@/services/physio-calendar-service.js";
import { Users } from "@/services/user-service.js";
import { PlayerInjury } from "@/services/player-Injury-service.js";
import { mapGetters } from "vuex";
import dayjs from "dayjs";

export default {
  name: "PhysioCalendar",
  components: {
    AppTemplate,
    SpinnerLoader,
    VueDatePicker,
  },
  data() {
    return {
      loadingStuff: false,
      loading: false,
      userData: {},
      physios: [],
      selectedPhysioId: null,
      weekStart: dayjs().startOf('week'),
      viewMode: 'calendar',
      availability: [],
      appointments: [],
      displayHours: [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],  // 7am-11pm
      // New slot form
      newSlot: {
        date: null,
        startTime: '08:00',
        endTime: '23:00',
        slotLength: 30
      },
      // Bulk add mode
      bulkMode: false,
      bulkSlot: {
        dateRange: null,
        startTime: '08:00',
        endTime: '23:00',
        slotLength: 30,
        selectedDays: [1, 2, 3, 4, 5] // Mon-Fri by default
      },
      dayNames: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      // Appointment modal
      showAppointmentModal: false,
      isBookingMode: false,
      selectedSlot: null,
      selectedAppointment: null,
      bookingForm: {
        playerId: '',
        reason: '',
        notes: ''
      },
      bookingLoading: false,
      // Players for booking
      allPlayers: [],
      injuredPlayers: [],
      // Confirmation modal
      showConfirmModal: false,
      confirmModal: {
        title: '',
        message: '',
        action: null,
        btnText: 'Confirm',
        btnClass: 'btn-danger'
      },
      // Responsive
      isMobile: window.innerWidth < 768,
      selectedDayIndex: 0,  // For mobile single-day view
      // Edit Appointment Modal
      showEditAppointmentModal: false,
      editingAppointmentData: null,
      editAppointmentForm: {
        wantReschedule: false,
        newDate: '',
        newStartTime: '',
        newEndTime: '',
        reason: '',
        notes: ''
      },
      editLoading: false,
      loadingRescheduleSlots: false,
      rescheduleAvailability: []
    };
  },
  computed: {
    ...mapGetters(["getProfile"]),
    isPhysio() {
      const pos = this.userData?.position;
      return pos === 'Physio' || pos === 'Head Physio';
    },
    isAdmin() {
      return this.userData?.userType === 'Admin';
    },
    canSelectPhysio() {
      return this.isAdmin || this.userData?.position === 'Head Physio';
    },
    canViewCalendar() {
      return this.isPhysio || this.isAdmin;
    },
    canBookForOthers() {
      // Admins, Physios, and Coaches can book on behalf of players
      const pos = this.userData?.position;
      return this.isAdmin || this.isPhysio || pos === 'Coach' || pos === 'HeadCoach';
    },
    selectedPhysioName() {
      const physio = this.physios.find(p => p.id === this.selectedPhysioId);
      return physio?.name || 'Unknown';
    },
    weekDays() {
      const days = [];
      for (let i = 0; i < 7; i++) {
        const date = this.weekStart.add(i, 'day');
        days.push({
          date: date.format('YYYY-MM-DD'),
          dayName: date.format('ddd'),
          displayDate: date.format('D MMM')
        });
      }
      return days;
    },
    formatWeekRange() {
      const end = this.weekStart.add(6, 'day');
      return `${this.weekStart.format('D MMM')} - ${end.format('D MMM YYYY')}`;
    },
    timeOptions() {
      const options = [];
      for (let h = 7; h <= 20; h++) {
        options.push(`${String(h).padStart(2, '0')}:00`);
        options.push(`${String(h).padStart(2, '0')}:30`);
      }
      return options;
    },
    endTimeOptions() {
      if (!this.newSlot.startTime) return this.timeOptions;
      const startIndex = this.timeOptions.indexOf(this.newSlot.startTime);
      return this.timeOptions.slice(startIndex + 1);
    },
    canAddSlot() {
      return this.newSlot.date && this.newSlot.startTime && this.newSlot.endTime &&
        this.newSlot.startTime < this.newSlot.endTime;
    },
    // Bulk mode computed
    bulkEndTimeOptions() {
      if (!this.bulkSlot.startTime) return this.timeOptions;
      const startIndex = this.timeOptions.indexOf(this.bulkSlot.startTime);
      return this.timeOptions.slice(startIndex + 1);
    },
    canAddBulkSlot() {
      return this.bulkSlot.dateRange &&
        this.bulkSlot.dateRange.length === 2 &&
        this.bulkSlot.startTime &&
        this.bulkSlot.endTime &&
        this.bulkSlot.startTime < this.bulkSlot.endTime &&
        this.bulkSlot.selectedDays.length > 0;
    },
    bulkPreviewCount() {
      if (!this.bulkSlot.dateRange || this.bulkSlot.dateRange.length !== 2) return 0;
      if (this.bulkSlot.selectedDays.length === 0) return 0;

      const startDate = dayjs(this.bulkSlot.dateRange[0]);
      const endDate = dayjs(this.bulkSlot.dateRange[1]);
      let count = 0;
      let current = startDate;

      while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
        if (this.bulkSlot.selectedDays.includes(current.day())) {
          count++;
        }
        current = current.add(1, 'day');
      }
      return count;
    },
    // Mobile: get currently selected day
    selectedDay() {
      return this.weekDays[this.selectedDayIndex] || this.weekDays[0];
    },
    minRescheduleDate() {
      return dayjs().format('YYYY-MM-DD');
    },
    canSaveAppointmentEdit() {
      // Can save if there are changes to reason/notes, or valid reschedule
      if (this.editAppointmentForm.wantReschedule) {
        return this.editAppointmentForm.newDate && this.editAppointmentForm.newStartTime;
      }
      // Allow save if reason or notes changed
      const hasReasonChange = this.editAppointmentForm.reason !== (this.editingAppointmentData?.reason || '');
      const hasNotesChange = this.editAppointmentForm.notes !== (this.editingAppointmentData?.notes || '');
      return hasReasonChange || hasNotesChange;
    }
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize);
    // Get user data from Vuex store
    if (this.getProfile) {
      this.userData = { ...this.getProfile };
      await this.loadPhysios();
      await this.loadCalendarData();
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    // Watch for profile to load from store
    getProfile: {
      handler(newVal) {
        if (newVal && !this.userData?.id) {
          this.userData = { ...newVal };
          this.loadPhysios();
        }
      },
      immediate: true
    },
    weekStart() {
      this.loadCalendarData();
    },
    'editAppointmentForm.newDate'(newDate) {
      if (newDate && this.editingAppointmentData) {
        this.loadRescheduleAvailability(newDate);
      } else {
        this.rescheduleAvailability = [];
      }
    }
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth < 768;
    },

    // Mobile day navigation
    previousDay() {
      if (this.selectedDayIndex > 0) {
        this.selectedDayIndex--;
      }
    },
    nextDay() {
      if (this.selectedDayIndex < this.weekDays.length - 1) {
        this.selectedDayIndex++;
      }
    },
    selectDay(index) {
      this.selectedDayIndex = index;
    },

    async loadPhysios() {
      try {
        // Validate clubId exists
        if (!this.userData.clubId) {
          console.error('No clubId found in user data');
          // If user is a physio, they can still use their own calendar
          if (this.isPhysio) {
            this.selectedPhysioId = this.userData.id;
            this.physios = [{ id: this.userData.id, name: this.userData.name || 'Me' }];
          }
          return;
        }

        console.log('Fetching physios with clubId:', this.userData.clubId);
        const response = await PhysioCalendarService.getAllPhysios(this.userData.clubId);
        console.log('getAllPhysios response:', response.data);

        // Handle error response
        if (response.data && response.data.error) {
          console.error('Error from API:', response.data.error, response.data);
          return;
        }

        // Handle debug response (no results found)
        if (response.data && response.data.debug) {
          console.warn('No physios found for clubId:', response.data.searched_clubId);
          this.physios = [];
          return;
        }

        this.physios = Array.isArray(response.data) ? response.data : [];

        // Set default physio based on user type
        if (this.isPhysio) {
          // Physios see their own calendar by default
          this.selectedPhysioId = this.userData.id;
        } else if (this.isAdmin && this.physios.length > 0) {
          // Admins see the first physio's calendar by default
          this.selectedPhysioId = this.physios[0].id;
        } else if (this.physios.length > 0) {
          this.selectedPhysioId = this.physios[0].id;
        }
      } catch (error) {
        console.error('Error loading physios:', error);
      }
    },

    async loadCalendarData() {
      if (!this.selectedPhysioId) {
        this.loading = false;
        return;
      }

      this.loading = true;
      try {
        const weekStartStr = this.weekStart.format('YYYY-MM-DD');
        const weekEndStr = this.weekStart.add(6, 'day').format('YYYY-MM-DD');

        const [availResponse, apptResponse] = await Promise.all([
          PhysioCalendarService.getAvailability({
            physio_id: this.selectedPhysioId,
            week_start: weekStartStr,
            week_end: weekEndStr
          }),
          PhysioCalendarService.getAppointments({
            physio_id: this.selectedPhysioId,
            week_start: weekStartStr,
            week_end: weekEndStr
          })
        ]);

        this.availability = Array.isArray(availResponse.data) ? availResponse.data : [];
        this.appointments = Array.isArray(apptResponse.data) ? apptResponse.data : [];
      } catch (error) {
        console.error('Error loading calendar data:', error);
      } finally {
        this.loading = false;
      }
    },

    previousWeek() {
      this.weekStart = this.weekStart.subtract(7, 'day');
    },

    nextWeek() {
      this.weekStart = this.weekStart.add(7, 'day');
    },

    goToToday() {
      this.weekStart = dayjs().startOf('week');
      // Also update mobile view to show today
      this.selectedDayIndex = dayjs().day(); // 0 = Sunday, 1 = Monday, etc.
    },

    isToday(dateStr) {
      return dayjs(dateStr).isSame(dayjs(), 'day');
    },

    isPast(dateStr) {
      return dayjs(dateStr).isBefore(dayjs(), 'day');
    },

    formatHour(hour) {
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:00 ${ampm}`;
    },

    formatTime(time) {
      if (!time) return '';
      const [hour, min] = time.split(':').map(Number);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${String(min).padStart(2, '0')} ${ampm}`;
    },

    formatDateFull(dateStr) {
      if (!dateStr) return '';
      return dayjs(dateStr).format('dddd, D MMMM YYYY');
    },

    getSlotsForHour(date, hour) {
      const slots = [];

      // Get availability for this date/hour
      const dayAvailability = this.availability.filter(a =>
        a.date === date &&
        parseInt(a.start_time.split(':')[0]) === hour
      );

      // Get appointments for this date/hour
      const dayAppointments = this.appointments.filter(a =>
        a.appointment_date === date &&
        parseInt(a.start_time.split(':')[0]) === hour
      );

      // Mark availability slots as available or booked
      dayAvailability.forEach(slot => {
        const isBooked = dayAppointments.some(appt =>
          appt.start_time === slot.start_time
        );

        if (isBooked) {
          const appt = dayAppointments.find(a => a.start_time === slot.start_time);
          slots.push({
            ...appt,
            type: 'booked'
          });
        } else {
          slots.push({
            ...slot,
            type: 'available'
          });
        }
      });

      return slots;
    },

    getSlotStyle(slot, hour) {
      const startMinute = parseInt(slot.start_time.split(':')[1]);
      const topOffset = (startMinute / 60) * 100;

      let duration = 30; // default
      if (slot.end_time) {
        const [endH, endM] = slot.end_time.split(':').map(Number);
        const [startH, startM] = slot.start_time.split(':').map(Number);
        duration = (endH * 60 + endM) - (startH * 60 + startM);
      }

      const height = (duration / 60) * 100;

      return {
        top: `${topOffset}%`,
        height: `${Math.min(height, 100)}%`
      };
    },

    getAvailabilityForDay(date) {
      return this.availability.filter(a => a.date === date);
    },

    isSlotBooked(date, startTime) {
      return this.appointments.some(a =>
        a.appointment_date === date && a.start_time === startTime
      );
    },

    async handleSlotClick(date, slot) {
      if (this.isPast(date)) return;

      this.selectedSlot = {
        date,
        ...slot
      };
      this.isBookingMode = true;
      this.bookingForm = { playerId: '', reason: '', notes: '' };
      this.showAppointmentModal = true;

      // Load players for booking if user can book for others
      if (this.canBookForOthers) {
        await this.loadPlayersForBooking();
      }
    },

    viewAppointment(appointment) {
      this.selectedAppointment = appointment;
      this.isBookingMode = false;
      this.showAppointmentModal = true;
    },

    closeAppointmentModal() {
      this.showAppointmentModal = false;
      this.selectedSlot = null;
      this.selectedAppointment = null;
    },

    handleOverlayClick(event, modalType) {
      // Only close if clicking directly on the overlay, not on any child elements
      // Check if the click target is exactly the overlay element
      const isOverlay = event.target.classList.contains('modal-overlay');
      if (!isOverlay) return;

      if (modalType === 'appointment') {
        this.closeAppointmentModal();
      } else if (modalType === 'confirm') {
        this.closeConfirmModal();
      }
    },

    async bookAppointment() {
      console.log('bookAppointment called', {
        selectedSlot: this.selectedSlot,
        canBookForOthers: this.canBookForOthers,
        bookingForm: this.bookingForm,
        userData: this.userData
      });

      if (!this.selectedSlot) {
        console.log('No selectedSlot, returning');
        return;
      }

      // Determine which player to book for
      const playerId = this.canBookForOthers && this.bookingForm.playerId
        ? this.bookingForm.playerId
        : this.userData.id;

      console.log('Booking for playerId:', playerId);
      this.bookingLoading = true;
      try {
        const response = await PhysioCalendarService.bookSlot({
          physio_id: this.selectedPhysioId,
          player_id: playerId,
          date: this.selectedSlot.date,
          start_time: this.selectedSlot.start_time,
          end_time: this.selectedSlot.end_time,
          reason: this.bookingForm.reason,
          notes: this.bookingForm.notes
        });

        console.log('bookSlot response:', response);

        if (response.data?.success) {
          const playerName = this.getPlayerName(playerId);
          this.$toast?.success(`Appointment booked for ${playerName}!`);
          this.closeAppointmentModal();
          await this.loadCalendarData();
        } else {
          this.$toast?.error(response.data?.message || 'Failed to book appointment');
        }
      } catch (error) {
        console.error('Error booking appointment:', error);
        this.$toast?.error('Error booking appointment');
      } finally {
        this.bookingLoading = false;
      }
    },

    async loadPlayersForBooking() {
      try {
        // Load all players first
        const playersResponse = await Users.find({
          action: 'getUserByClubId',
          clubId: this.userData.clubId
        });
        if (playersResponse.data) {
          this.allPlayers = playersResponse.data.filter(u =>
            u.userType === 'Player' || u.position === 'Player'
          );
        }

        // Load injured players (those with active/current injuries)
        const injuredResponse = await PlayerInjury.find({
          action: 'getInjuryedPlayersAllByClubId',
          clubId: this.userData.clubId
        });
        if (injuredResponse.data && Array.isArray(injuredResponse.data)) {
          // Filter to only show players with active injuries (not fully recovered)
          // and enrich with player names from allPlayers
          const activeInjuries = injuredResponse.data.filter(p =>
            p.playing_status !== 'Full' || p.training_status !== 'Full Training'
          );

          // Map injured players - API now returns player_name from JOIN
          this.injuredPlayers = activeInjuries.map(injury => ({
            ...injury,
            id: injury.player_id, // Use player_id as id for the select value
            name: injury.player_name || 'Unknown Player'
          }));
        }
      } catch (error) {
        console.error('Error loading players:', error);
      }
    },

    getPlayerName(playerId) {
      if (playerId === this.userData.id) {
        return this.userData.name || 'yourself';
      }
      const player = this.allPlayers.find(p => p.id === playerId) ||
        this.injuredPlayers.find(p => p.id === playerId);
      return player?.name || 'the player';
    },

    async addAvailabilitySlots() {
      if (!this.canAddSlot) return;

      const dateStr = dayjs(this.newSlot.date).format('YYYY-MM-DD');
      const slots = [];

      // Generate individual slots from start to end time
      let currentTime = this.newSlot.startTime;
      while (currentTime < this.newSlot.endTime) {
        const [h, m] = currentTime.split(':').map(Number);
        const endMinutes = h * 60 + m + this.newSlot.slotLength;
        const endH = Math.floor(endMinutes / 60);
        const endM = endMinutes % 60;
        const endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;

        if (endTime <= this.newSlot.endTime) {
          slots.push({
            start_time: currentTime,
            end_time: endTime
          });
        }

        currentTime = endTime;
      }

      this.loadingStuff = true;
      try {
        for (const slot of slots) {
          await PhysioCalendarService.addAvailability({
            physio_id: this.selectedPhysioId,
            date: dateStr,
            start_time: slot.start_time,
            end_time: slot.end_time,
            slot_length_minutes: this.newSlot.slotLength
          });
        }

        this.$toast?.success(`Added ${slots.length} availability slots`);
        this.newSlot.date = null;
        await this.loadCalendarData();
      } catch (error) {
        console.error('Error adding availability:', error);
        this.$toast?.error('Error adding availability');
      } finally {
        this.loadingStuff = false;
      }
    },

    // Bulk mode methods
    selectWeekdays() {
      this.bulkSlot.selectedDays = [1, 2, 3, 4, 5]; // Mon-Fri
    },

    selectAllDays() {
      this.bulkSlot.selectedDays = [0, 1, 2, 3, 4, 5, 6]; // Sun-Sat
    },

    clearDays() {
      this.bulkSlot.selectedDays = [];
    },

    async addBulkAvailability() {
      if (!this.canAddBulkSlot) return;

      const startDate = dayjs(this.bulkSlot.dateRange[0]);
      const endDate = dayjs(this.bulkSlot.dateRange[1]);
      const datesToAdd = [];

      // Collect all dates that match selected days
      let current = startDate;
      while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
        if (this.bulkSlot.selectedDays.includes(current.day())) {
          datesToAdd.push(current.format('YYYY-MM-DD'));
        }
        current = current.add(1, 'day');
      }

      if (datesToAdd.length === 0) {
        this.$toast?.warning('No matching days in the selected date range');
        return;
      }

      // Generate slots for each date
      const allSlots = [];
      for (const dateStr of datesToAdd) {
        let currentTime = this.bulkSlot.startTime;
        while (currentTime < this.bulkSlot.endTime) {
          const [h, m] = currentTime.split(':').map(Number);
          const endMinutes = h * 60 + m + this.bulkSlot.slotLength;
          const endH = Math.floor(endMinutes / 60);
          const endM = endMinutes % 60;
          const endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;

          if (endTime <= this.bulkSlot.endTime) {
            allSlots.push({
              date: dateStr,
              start_time: currentTime,
              end_time: endTime
            });
          }
          currentTime = endTime;
        }
      }

      this.loadingStuff = true;
      let successCount = 0;

      try {
        for (const slot of allSlots) {
          try {
            await PhysioCalendarService.addAvailability({
              physio_id: this.selectedPhysioId,
              date: slot.date,
              start_time: slot.start_time,
              end_time: slot.end_time,
              slot_length_minutes: this.bulkSlot.slotLength
            });
            successCount++;
          } catch (slotError) {
            console.warn('Error adding slot:', slotError);
          }
        }

        this.$toast?.success(`Added availability for ${datesToAdd.length} days (${successCount} slots)`);
        this.bulkSlot.dateRange = null;
        await this.loadCalendarData();
      } catch (error) {
        console.error('Error adding bulk availability:', error);
        this.$toast?.error('Error adding bulk availability');
      } finally {
        this.loadingStuff = false;
      }
    },

    confirmRemoveSlot(date, slot) {
      this.confirmModal = {
        title: 'Remove Availability',
        message: `Remove availability on ${this.formatDateFull(date)} at ${this.formatTime(slot.start_time)}?`,
        action: () => this.removeSlot(date, slot),
        btnText: 'Remove',
        btnClass: 'btn-danger'
      };
      this.showConfirmModal = true;
    },

    async removeSlot(date, slot) {
      this.closeConfirmModal();
      this.loadingStuff = true;

      try {
        const response = await PhysioCalendarService.removeAvailability({
          physio_id: this.selectedPhysioId,
          date: date,
          start_time: slot.start_time
        });

        if (response.data?.success) {
          this.$toast?.success('Availability removed');
          await this.loadCalendarData();
        } else {
          this.$toast?.error(response.data?.message || 'Failed to remove slot');
        }
      } catch (error) {
        console.error('Error removing slot:', error);
        this.$toast?.error('Error removing availability');
      } finally {
        this.loadingStuff = false;
      }
    },

    confirmClearWeek() {
      this.confirmModal = {
        title: 'Clear Week Availability',
        message: 'Remove all unbooked availability slots for this week? This cannot be undone.',
        action: () => this.clearWeekAvailability(),
        btnText: 'Clear All',
        btnClass: 'btn-danger'
      };
      this.showConfirmModal = true;
    },

    async clearWeekAvailability() {
      this.closeConfirmModal();
      this.loadingStuff = true;

      try {
        const weekStartStr = this.weekStart.format('YYYY-MM-DD');
        const weekEndStr = this.weekStart.add(6, 'day').format('YYYY-MM-DD');

        const response = await PhysioCalendarService.bulkRemoveAvailability({
          physio_id: this.selectedPhysioId,
          start_date: weekStartStr,
          end_date: weekEndStr
        });

        if (response.data?.success) {
          this.$toast?.success(`Removed ${response.data.rows_affected || 0} slots`);
          await this.loadCalendarData();
        }
      } catch (error) {
        console.error('Error clearing availability:', error);
        this.$toast?.error('Error clearing availability');
      } finally {
        this.loadingStuff = false;
      }
    },

    confirmCancelAppointment() {
      this.closeAppointmentModal();
      this.confirmModal = {
        title: 'Cancel Appointment',
        message: `Cancel appointment with ${this.selectedAppointment?.player_name}? They will be notified.`,
        action: () => this.cancelAppointment(),
        btnText: 'Cancel Appointment',
        btnClass: 'btn-danger'
      };
      this.showConfirmModal = true;
    },

    async cancelAppointment() {
      this.closeConfirmModal();
      this.loadingStuff = true;

      try {
        const response = await PhysioCalendarService.cancelAppointment({
          appointment_id: this.selectedAppointment.id,
          user_id: this.userData.id,
          user_type: this.userData.userType,
          send_notification: true
        });

        if (response.data?.success) {
          this.$toast?.success('Appointment cancelled');
          await this.loadCalendarData();
        } else {
          this.$toast?.error(response.data?.message || 'Failed to cancel appointment');
        }
      } catch (error) {
        console.error('Error cancelling appointment:', error);
        this.$toast?.error('Error cancelling appointment');
      } finally {
        this.loadingStuff = false;
        this.selectedAppointment = null;
      }
    },

    closeConfirmModal() {
      this.showConfirmModal = false;
    },

    // Edit Appointment Methods
    openEditAppointmentModal() {
      if (!this.selectedAppointment) return;

      this.editingAppointmentData = { ...this.selectedAppointment };
      this.editAppointmentForm = {
        wantReschedule: false,
        newDate: '',
        newStartTime: '',
        newEndTime: '',
        reason: this.selectedAppointment.reason || '',
        notes: this.selectedAppointment.notes || ''
      };
      this.rescheduleAvailability = [];
      this.closeAppointmentModal();
      this.showEditAppointmentModal = true;
    },

    closeEditAppointmentModal() {
      this.showEditAppointmentModal = false;
      this.editingAppointmentData = null;
      this.rescheduleAvailability = [];
    },

    handleEditOverlayClick(event) {
      if (event.target.classList.contains('modal-overlay')) {
        this.closeEditAppointmentModal();
      }
    },

    async loadRescheduleAvailability(date) {
      if (!this.editingAppointmentData?.physio_id) return;

      this.loadingRescheduleSlots = true;
      this.rescheduleAvailability = [];
      this.editAppointmentForm.newStartTime = '';
      this.editAppointmentForm.newEndTime = '';

      try {
        // Load availability and appointments for the selected date
        const [availResponse, apptResponse] = await Promise.all([
          PhysioCalendarService.getAvailability({
            physio_id: this.editingAppointmentData.physio_id,
            week_start: date,
            week_end: date
          }),
          PhysioCalendarService.getAppointments({
            physio_id: this.editingAppointmentData.physio_id,
            week_start: date,
            week_end: date
          })
        ]);

        const dayAvailability = Array.isArray(availResponse.data) ? availResponse.data : [];
        const dayAppointments = Array.isArray(apptResponse.data) ? apptResponse.data : [];

        // Helper to normalize time format
        const normalizeTime = (time) => {
          if (!time) return '';
          return time.substring(0, 5);
        };

        // Filter out booked slots (except the current appointment)
        this.rescheduleAvailability = dayAvailability.filter(slot => {
          const slotTime = normalizeTime(slot.start_time);
          return !dayAppointments.some(apt =>
            apt.id !== this.editingAppointmentData.id &&
            normalizeTime(apt.start_time) === slotTime
          );
        });
      } catch (error) {
        console.error('Error loading reschedule availability:', error);
      } finally {
        this.loadingRescheduleSlots = false;
      }
    },

    selectRescheduleSlot(slot) {
      this.editAppointmentForm.newStartTime = slot.start_time;
      this.editAppointmentForm.newEndTime = slot.end_time;
    },

    async saveEditedAppointment() {
      if (!this.editingAppointmentData || !this.canSaveAppointmentEdit) return;

      this.editLoading = true;
      try {
        const updateData = {
          appointment_id: this.editingAppointmentData.id,
          reason: this.editAppointmentForm.reason,
          notes: this.editAppointmentForm.notes
        };

        // Add reschedule data if user wants to reschedule
        if (this.editAppointmentForm.wantReschedule && this.editAppointmentForm.newDate && this.editAppointmentForm.newStartTime) {
          updateData.new_date = this.editAppointmentForm.newDate;
          updateData.new_start_time = this.editAppointmentForm.newStartTime;
          updateData.new_end_time = this.editAppointmentForm.newEndTime;
        }

        const response = await PhysioCalendarService.updateAppointment(updateData);

        if (response.data?.success) {
          const message = this.editAppointmentForm.wantReschedule
            ? 'Appointment rescheduled successfully!'
            : 'Appointment updated successfully!';
          this.$toast?.success(message);
          this.closeEditAppointmentModal();
          await this.loadCalendarData();
        } else {
          this.$toast?.error(response.data?.message || 'Failed to update appointment');
        }
      } catch (error) {
        console.error('Error updating appointment:', error);
        this.$toast?.error('Error updating appointment');
      } finally {
        this.editLoading = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.physio-calendar-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  h2 {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;

    i {
      color: #007bff;
    }
  }

  .subtitle {
    margin: 4px 0 0 0;
    color: #6c757d;
    font-size: 14px;
  }
}

.physio-selector {
  display: flex;
  align-items: center;
  gap: 10px;

  label {
    font-weight: 500;
    white-space: nowrap;
  }

  select {
    min-width: 180px;
  }
}

.week-navigation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  .current-week {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 18px;
    font-weight: 600;
    min-width: 200px;
    justify-content: center;
  }
}

.view-controls {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #6c757d;

  p {
    margin-top: 12px;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  text-align: center;
  color: #6c757d;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 20px;

  h4 {
    margin-top: 16px;
    color: #343a40;
  }

  p {
    max-width: 400px;
    margin-top: 8px;
  }
}

/* Calendar Grid */
.calendar-view {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.calendar-grid {
  display: flex;
  min-height: 600px;
}

.time-column {
  width: 70px;
  flex-shrink: 0;
  border-right: 1px solid #e9ecef;

  .day-header {
    height: 60px;
    border-bottom: 1px solid #e9ecef;
  }

  .time-slot {
    height: 60px;
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    padding: 4px 8px;
    font-size: 11px;
    color: #6c757d;
    border-bottom: 1px solid #f0f0f0;
  }
}

.day-column {
  flex: 1;
  min-width: 100px;
  border-right: 1px solid #e9ecef;

  &:last-child {
    border-right: none;
  }

  &.is-today {
    background: #f0f7ff;

    .day-header {
      background: #007bff;
      color: white;
    }
  }

  &.is-past {
    opacity: 0.6;
  }

  .day-header {
    height: 60px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid #e9ecef;
    background: #f8f9fa;

    .day-name {
      font-weight: 600;
      font-size: 14px;
    }

    .day-date {
      font-size: 12px;
    }
  }
}

.slots-container {
  position: relative;
}

.hour-block {
  height: 60px;
  border-bottom: 1px solid #f0f0f0;
  position: relative;
}

.slot {
  position: absolute;
  left: 2px;
  right: 2px;
  border-radius: 4px;
  padding: 4px 6px;
  font-size: 11px;
  cursor: pointer;
  overflow: hidden;
  z-index: 1;
  transition: transform 0.1s;

  &:hover {
    transform: scale(1.02);
    z-index: 2;
  }

  .slot-time {
    display: block;
    font-weight: 600;
    font-size: 10px;
  }

  .slot-label,
  .slot-patient {
    display: block;
    font-size: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .slot-reason {
    display: block;
    font-size: 9px;
    opacity: 0.8;
  }
}

.available-slot {
  background: #d4edda;
  border-left: 3px solid #28a745;
  color: #155724;
}

.booked-slot {
  background: #cce5ff;
  border-left: 3px solid #007bff;
  color: #004085;
}

/* Availability View */
.availability-view {
  max-width: 1200px;
  margin: 0 auto;
}

.availability-header {
  margin-bottom: 24px;

  h4 {
    margin-bottom: 4px;
  }
}

.quick-add-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  h5 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;

    i {
      color: #28a745;
    }
  }
}

.quick-add-form .form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;

  .form-group {
    flex: 1;
    min-width: 150px;

    label {
      display: block;
      margin-bottom: 6px;
      font-size: 13px;
      font-weight: 500;
    }

    &.form-group-btn {
      flex: 0 0 auto;
    }
  }
}

.mode-toggle {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;

  .btn {
    display: flex;
    align-items: center;
    gap: 6px;
  }
}

.bulk-add-form {
  .days-selection {
    margin-top: 16px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 8px;

    > label {
      display: block;
      margin-bottom: 12px;
      font-weight: 500;
    }
  }

  .day-checkboxes {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 12px;
  }

  .day-checkbox {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: #0d6efd;
    }

    input:checked + span {
      color: #0d6efd;
      font-weight: 500;
    }

    input {
      cursor: pointer;
    }
  }

  .quick-select-btns {
    display: flex;
    gap: 8px;
  }

  .bulk-preview {
    margin-top: 16px;
    padding: 12px 16px;
    background: #e7f3ff;
    border-radius: 8px;
    color: #0d6efd;
    display: flex;
    align-items: center;
    gap: 8px;

    i {
      font-size: 1.1em;
    }
  }

  .form-group-btn {
    margin-top: 16px;
  }
}

.week-availability {
  margin-bottom: 24px;

  h5 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;

    i {
      color: #007bff;
    }
  }
}

.week-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
}

.day-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;

  &.is-past {
    opacity: 0.6;
  }
}

.day-card-header {
  padding: 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;

  .day-name {
    font-weight: 600;
  }

  .day-date {
    color: #6c757d;
    font-size: 13px;
  }
}

.day-card-body {
  padding: 12px;
  min-height: 100px;
}

.no-slots {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  padding: 20px;

  i {
    font-size: 24px;
    margin-bottom: 8px;
  }

  span {
    font-size: 13px;
  }
}

.slot-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.slot-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: #e8f5e9;
  border-radius: 6px;
  font-size: 13px;

  &.is-booked {
    background: #e3f2fd;
  }

  .slot-time {
    font-weight: 500;
  }

  .booked-badge {
    font-size: 11px;
    color: #007bff;
    font-weight: 500;
  }
}

.bulk-actions {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  h5 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;

    i {
      color: #ffc107;
    }
  }
}

/* Modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-dialog {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow: visible;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 10000;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
  position: relative;
  z-index: 10;

  h4 {
    margin: 0;
    font-size: 18px;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 28px;
    cursor: pointer;
    color: #6c757d;
    line-height: 1;
    pointer-events: auto;
    z-index: 20;
    padding: 4px 8px;

    &:hover {
      color: #000;
    }
  }
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  overflow-x: visible;

  .form-group {
    position: relative;
    z-index: 1;
  }

  .form-select,
  select,
  textarea,
  input,
  .form-control {
    position: relative;
    z-index: 10;
    pointer-events: auto;
    cursor: text;
    background-color: #fff;

    &:not(:disabled) {
      opacity: 1;
    }
  }

  .form-select,
  select {
    cursor: pointer;
  }

  .btn {
    position: relative;
    z-index: 10;
    pointer-events: auto;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      pointer-events: auto;
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e9ecef;
  position: relative;
  z-index: 10;

  .btn {
    position: relative;
    z-index: 10;
    pointer-events: auto;
    cursor: pointer;
  }
}

.booking-info {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;

  p {
    margin: 0 0 8px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.appointment-details {
  .detail-row {
    display: flex;
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }

    label {
      width: 100px;
      font-weight: 500;
      color: #6c757d;
      flex-shrink: 0;
    }

    span {
      flex: 1;
    }
  }
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  &.booked {
    background: #cce5ff;
    color: #004085;
  }

  &.cancelled {
    background: #f8d7da;
    color: #721c24;
  }

  &.completed {
    background: #d4edda;
    color: #155724;
  }
}

.appointment-actions {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  .btn {
    pointer-events: auto;
  }
}

.btn-block {
  width: 100%;
}

.form-group {
  margin-bottom: 16px;

  label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    font-size: 14px;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .week-navigation {
    flex-direction: column;
    gap: 12px;

    .btn {
      width: 100%;
    }

    .current-week {
      order: -1;
    }
  }

  .calendar-grid {
    overflow-x: auto;
  }

  .day-column {
    min-width: 80px;
  }

  .quick-add-form .form-row {
    flex-direction: column;

    .form-group {
      width: 100%;
    }
  }

  .week-grid {
    grid-template-columns: 1fr;
  }

  .view-controls {
    .btn-group {
      width: 100%;
      display: flex;

      .btn {
        flex: 1;
        font-size: 13px;
        padding: 8px;
      }
    }
  }
}

/* Mobile Day Selector */
.mobile-day-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  background: white;
  padding: 8px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  .day-nav-btn {
    width: 36px;
    height: 36px;
    border: none;
    background: #f8f9fa;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #495057;
    flex-shrink: 0;

    &:disabled {
      opacity: 0.3;
    }

    &:not(:disabled):active {
      background: #e9ecef;
    }
  }

  .day-tabs {
    display: flex;
    flex: 1;
    gap: 4px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .day-tab {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 6px 10px;
    border: none;
    background: transparent;
    border-radius: 8px;
    min-width: 44px;
    flex-shrink: 0;

    .day-abbr {
      font-size: 11px;
      color: #6c757d;
      text-transform: uppercase;
    }

    .day-num {
      font-size: 16px;
      font-weight: 600;
      color: #495057;
    }

    &.active {
      background: #0d6efd;

      .day-abbr,
      .day-num {
        color: white;
      }
    }

    &.is-today:not(.active) {
      background: #e7f3ff;

      .day-num {
        color: #0d6efd;
      }
    }

    &.is-past:not(.active) {
      opacity: 0.5;
    }
  }
}

/* Mobile Calendar */
.mobile-calendar {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;

  .mobile-day-header {
    padding: 16px;
    background: linear-gradient(135deg, #0d6efd, #0056b3);
    color: white;
    text-align: center;

    .day-name {
      display: block;
      font-size: 14px;
      opacity: 0.9;
      margin-bottom: 4px;
    }

    .day-date {
      display: block;
      font-size: 20px;
      font-weight: 600;
    }
  }

  .mobile-slots-list {
    padding: 12px;
    max-height: 60vh;
    overflow-y: auto;
  }

  .mobile-hour-row {
    display: flex;
    align-items: flex-start;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }

    .hour-label {
      width: 60px;
      font-size: 13px;
      color: #6c757d;
      font-weight: 500;
      padding-top: 8px;
      flex-shrink: 0;
    }

    .hour-slots {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .no-slots-hour {
      color: #dee2e6;
      padding: 8px;
      text-align: center;
    }
  }

  .mobile-slot {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 10px;
    cursor: pointer;
    transition: transform 0.15s ease;

    &:active {
      transform: scale(0.98);
    }

    i {
      font-size: 18px;
      flex-shrink: 0;
    }

    .time {
      font-weight: 500;
      font-size: 14px;
    }

    .slot-info {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;

      .patient {
        font-size: 13px;
        opacity: 0.8;
      }
    }

    .status {
      font-size: 11px;
      padding: 4px 8px;
      border-radius: 12px;
      font-weight: 500;
      flex-shrink: 0;
    }

    &.available {
      background: linear-gradient(135deg, #d4edda, #c3e6cb);
      color: #155724;

      .status {
        background: rgba(255, 255, 255, 0.6);
      }
    }

    &.booked {
      background: linear-gradient(135deg, #cce5ff, #b8daff);
      color: #004085;

      .status {
        background: rgba(255, 255, 255, 0.6);
      }
    }
  }
}

/* Hide desktop calendar on mobile, show on desktop */
@media (max-width: 767px) {
  .desktop-calendar {
    display: none !important;
  }
}

@media (min-width: 768px) {

  .mobile-day-selector,
  .mobile-calendar {
    display: none !important;
  }
}

/* Edit Appointment Modal Styles */
.edit-appointment-modal {
  max-width: 550px;
}

.current-appointment-info {
  background: #e7f3ff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  border-left: 4px solid #0d6efd;

  h6 {
    margin: 0 0 12px 0;
    color: #0d6efd;
    font-weight: 600;
  }

  .info-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 6px 0;

    i {
      color: #0d6efd;
      width: 18px;
    }
  }
}

.reschedule-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;

  .form-check-label {
    cursor: pointer;
  }

  .reschedule-options {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;
  }
}

.time-slots-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.time-slot-btn {
  background: #e9ecef;
  border: 2px solid transparent;
  color: #495057;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;

  &:hover {
    background: #dee2e6;
    border-color: #0d6efd;
  }

  &.selected {
    background: #0d6efd;
    color: white;
    border-color: #0d6efd;
  }
}

.no-slots-message {
  color: #dc3545;
  font-size: 0.9em;
  padding: 12px;
  background: #f8d7da;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;

  i {
    font-size: 1.1em;
  }
}

.loading-slots {
  color: #6c757d;
  font-size: 0.9em;
  padding: 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
